import { codePickBody, codePicksFromRecord, emptyCodePicks } from "./codePicks";
import { firstOfMonth } from "./format";
import type { Call, CallCreate, CodePicks, LandedPriceUse } from "./types";

export const FMP_MIN_USD_PER_LB = 0.001;
export const FMP_MAX_USD_PER_LB = 10;
export const RATIONALE_MAX_CHARS = 5000;
export const REVISIT_TRIGGER_MAX_CHARS = 2000;
export const PERIOD_OFFSETS = [0, 1, 2, 3] as const;

export interface PeriodDraft {
  month: string;
  fmpUsdPerLb: string;
  rationale: string;
  revisitTrigger: string;
}

export interface CallDraft extends CodePicks {
  callMonth: string;
  landedPriceUseCode: LandedPriceUse | "";
  periods: PeriodDraft[];
}

export function shiftMonth(month: string, monthsToAdd: number): string {
  const [year, monthNumber] = month.split("-").map(Number);
  const monthIndex = year * 12 + (monthNumber - 1) + monthsToAdd;
  const shiftedYear = Math.floor(monthIndex / 12);
  const shiftedMonthNumber = (monthIndex % 12) + 1;
  return `${shiftedYear}-${String(shiftedMonthNumber).padStart(2, "0")}`;
}

export function emptyDraft(callMonth: string): CallDraft {
  return {
    callMonth,
    ...emptyCodePicks(),
    landedPriceUseCode: "",
    periods: PERIOD_OFFSETS.map((periodOffset) => ({
      month: shiftMonth(callMonth, periodOffset),
      fmpUsdPerLb: "",
      rationale: "",
      revisitTrigger: "",
    })),
  };
}

export function draftFromSavedCall(savedCall: Call): CallDraft {
  const draft = emptyDraft(savedCall.callMonth.slice(0, 7));
  for (const savedPeriod of savedCall.periods) {
    draft.periods[savedPeriod.periodOffset] = {
      month: savedPeriod.periodMonth.slice(0, 7),
      fmpUsdPerLb: String(savedPeriod.fmpUsdPerLb),
      rationale: savedPeriod.rationale,
      revisitTrigger: savedPeriod.revisitTrigger,
    };
  }
  return {
    ...draft,
    ...codePicksFromRecord(savedCall),
    landedPriceUseCode: savedCall.landedPriceUseCode as LandedPriceUse,
  };
}

export function periodLabel(periodOffset: number): string {
  if (periodOffset === 0) return "This month · assessment";
  return `Forecast · +${periodOffset} month${periodOffset > 1 ? "s" : ""}`;
}

export function findDraftProblem(draft: CallDraft): string | null {
  for (const [periodOffset, period] of draft.periods.entries()) {
    const periodName = periodLabel(periodOffset);
    const fmpUsdPerLb = Number(period.fmpUsdPerLb);
    if (period.fmpUsdPerLb.trim() === "" || !Number.isFinite(fmpUsdPerLb)) {
      return `${periodName}: enter your FMP.`;
    }
    if (fmpUsdPerLb < FMP_MIN_USD_PER_LB || fmpUsdPerLb > FMP_MAX_USD_PER_LB) {
      return `${periodName}: FMP must be between $${FMP_MIN_USD_PER_LB} and $${FMP_MAX_USD_PER_LB}/lb.`;
    }
    if (!period.rationale.trim()) return `${periodName}: explain why this price.`;
    if (!period.revisitTrigger.trim()) return `${periodName}: say what would change your view.`;
    if (period.rationale.length > RATIONALE_MAX_CHARS) {
      return `${periodName}: keep "Why this price?" under ${RATIONALE_MAX_CHARS} characters.`;
    }
    if (period.revisitTrigger.length > REVISIT_TRIGGER_MAX_CHARS) {
      return `${periodName}: keep "What would change my view?" under ${REVISIT_TRIGGER_MAX_CHARS} characters.`;
    }
  }
  if (!draft.landedPriceUseCode) return "Answer the landed-price question.";
  return null;
}

export function toCreateRequest(draft: CallDraft, resinFamilyCode: string): CallCreate {
  return {
    callMonth: firstOfMonth(draft.callMonth),
    resinFamilyCode,
    ...codePickBody(draft),
    landedPriceUseCode: draft.landedPriceUseCode as LandedPriceUse,
    periods: draft.periods.map((period) => ({
      periodMonth: firstOfMonth(period.month),
      fmpUsdPerLb: Number(period.fmpUsdPerLb),
      rationale: period.rationale.trim(),
      revisitTrigger: period.revisitTrigger.trim(),
    })),
  };
}

export function changeInCentsPerLb(fmpUsdPerLb: number, priorFmpUsdPerLb: number): number {
  const changeInCents = (fmpUsdPerLb - priorFmpUsdPerLb) * 100;
  return Math.round(changeInCents * 10000) / 10000;
}

export function isOutsideNominationRange(
  changeInCents: number,
  highestNominationCentsPerLb: number | null | undefined,
): boolean {
  if (highestNominationCentsPerLb == null || highestNominationCentsPerLb <= 0) return false;
  return changeInCents < 0 || changeInCents > highestNominationCentsPerLb;
}

export function formatUsdPerLb(usdPerLb: number): string {
  return `$${usdPerLb}/lb`;
}

export function formatSignedCentsPerLb(centsPerLb: number): string {
  return `${centsPerLb > 0 ? "+" : ""}${centsPerLb}¢/lb`;
}
